import { CustomerTheme } from './schema';

/**
 * Derives a CSS custom property name from a theme path, so the Chakra theme and the runtime
 * injector cannot drift: both call this with the same path string.
 *
 *   'colors.primary.500'  -> '--peli-colors-primary-500'
 *   'heading.h1.fontSize' -> '--peli-heading-h1-font-size'
 */
export const toCssVar = (path: string) =>
  `--peli-${path
    .replace(/\./g, '-')
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .toLowerCase()}`;

/**
 * A `var()` reference with the current static value as its fallback. This is what makes the
 * override layer additive: with no DB row the fallback renders, so first paint is unchanged and
 * nothing has to be seeded.
 *
 * Put these inside the *token values* (e.g. `colors.primary[500]`), not at call sites - Chakra
 * emits `--chakra-colors-primary-500: var(--peli-colors-primary-500, #ED7500)`, so every consumer
 * of the token, including `colorScheme="primary"`, resolves through the one indirection.
 *
 * Do NOT use for `breakpoints`: those are consumed inside `@media (min-width: ...)`, where `var()`
 * is invalid CSS.
 */
export const cssVar = (path: string, fallback: string) => `var(${toCssVar(path)}, ${fallback})`;

/**
 * Builds a `cssVar` that prefers a tenant's own static token over the built-in default.
 *
 * This is what gives the theme three layers rather than two:
 *
 *   1. the built-in default passed at the call site - Chakra's, or whatever the app rendered before
 *   2. `customerTheme.tokens` from `@pelicargo/tenants`, baked in when the Chakra theme is built,
 *      so it is correct on the first paint and costs nothing at runtime
 *   3. the DB theme, injected as `--peli-*` once it arrives
 *
 * Takes the tokens as an argument rather than reading them itself: resolving a tenant needs
 * `window.location`, which cannot live in a shared package. The customer app supplies them.
 */
export const createThemeVar =
  (tokens: Record<string, unknown>) =>
  (path: string, builtInDefault: string): string => {
    const staticValue = path
      .split('.')
      .reduce<unknown>((node, key) => (node as Record<string, unknown>)?.[key], tokens);
    const fallback = staticValue === undefined || staticValue === null ? builtInDefault : String(staticValue);

    return cssVar(path, fallback);
  };

// Paths that are deliberately NOT CSS variables. `logo.*` is layout applied as React props;
// `fonts.url` is a stylesheet <link>; `copy`/`assets` are content, not paint.
const NON_CSS_SECTIONS = new Set(['logo', 'copy', 'assets']);
const NON_CSS_PATHS = new Set(['fonts.url']);

/**
 * Flattens a theme into the custom properties to set. Absent values are **omitted**, never
 * emitted as empty: `--peli-x: ;` does not trigger the `var()` fallback - the property becomes
 * invalid-at-computed-value-time, which can render text transparent.
 */
export const flattenThemeToCssVars = (theme?: CustomerTheme | null): Record<string, string> => {
  const out: Record<string, string> = {};
  if (!theme) return out;

  const walk = (value: unknown, path: string[]) => {
    if (value === undefined || value === null || value === '') return;
    if (path.length && NON_CSS_SECTIONS.has(path[0])) return;

    const joined = path.join('.');
    if (NON_CSS_PATHS.has(joined)) return;

    if (typeof value === 'object') {
      for (const [key, child] of Object.entries(value as Record<string, unknown>)) walk(child, [...path, key]);
      return;
    }

    out[toCssVar(joined)] = String(value);
  };

  walk(theme, []);
  return out;
};
